import { Injectable, inject, signal, computed } from '@angular/core';
import { AuthLogin, AuthLoginResponse, TokenInformation } from '../models/auth.model';
import { Router } from '@angular/router';
import { Routes } from '@core/routes/routes';
import { AuthApi } from './auth-api';
import { UserApi } from '@features/user/services/user-api';
import { Observable, BehaviorSubject, catchError, of, switchMap, tap, throwError, take, map } from 'rxjs';
import { DataEndpoint } from '@core/models/general.model';
import { BusinessStateService } from '@core/services/business-state.service';
import { UserDetailModel } from '@features/user/models/user.model';
import { HttpErrorResponse } from '@angular/common/http';

export enum UserRole {
  ADMIN = 'Admin',
  BUSSINESSMEN = 'Businessmen',
}

export enum UserPermission {
  CREATE_ACTIVITY = 'CREATE_ACTIVITY',
  CREATE_OFFICE = 'CREATE_OFFICE',
  UPDATE_OFFICE = 'UPDATE_OFFICE',
  DELETE_OFFICE = 'DELETE_OFFICE'
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private authApi = inject(AuthApi);
  private userApi = inject(UserApi);
  private readonly businessStateService = inject(BusinessStateService);

  private readonly TOKEN_KEY = 'token';
  private readonly REMEMBER_ME_KEY = 'rememberMe';
  private readonly USERNAME_KEY = 'rememberedUser';
  private readonly CONVERSION_KEY = 'conversion';
  private readonly GWP_KEY = 'globalWarmingPotential';
  private readonly BUSINESS_SELECTION_KEY = 'businessSelection';
  private readonly SELECTED_BUSINESS_KEY = 'selectedBusiness';

  // Observable pattern
  private userSubject = new BehaviorSubject<UserDetailModel | null>(null);
  public user$ = this.userSubject.asObservable();

  // Signals
  public userSignal = signal<UserDetailModel | null>(null);
  public isLoadingUser = signal<boolean>(false);
  public isInitialized = signal<boolean>(false);

  // Computed values
  public currentRole = computed(() => this.userSignal()?.Role);
  public isAdmin = computed(() => this.userSignal()?.Role === UserRole.ADMIN);
  public userName = computed(() => {
    const user = this.userSignal();
    return user ? `${user.Name} ${user.LastName}` : '';
  });

  login(
    credentials: AuthLogin,
    rememberMe?: boolean
  ): Observable<DataEndpoint<AuthLoginResponse>> {
    return this.authApi.postLogin(credentials).pipe(
      tap(response => {
        if (response.Status !== 200) return;

        // Guardar token según preferencia
        if (rememberMe) {
          localStorage.setItem(this.TOKEN_KEY, response.Data!.AccessToken);
          localStorage.setItem(this.REMEMBER_ME_KEY, 'true');
          localStorage.setItem(this.USERNAME_KEY, credentials.UserName);
        } else {
          sessionStorage.setItem(this.TOKEN_KEY, response.Data!.AccessToken);
          localStorage.removeItem(this.REMEMBER_ME_KEY);
          localStorage.removeItem(this.USERNAME_KEY);
        }
      }),
      switchMap((loginResponse) =>
        this.loadUserProfile().pipe(
          map(() => loginResponse)
        )
      ),
      tap(() => {
        this.router.navigate([Routes.dashboardPage]);
      }),
      catchError(error => {
        console.error('Error during login process:', error);
        this.logout();
        return throwError(() => error);
      })
    );
  }

  loadUserProfile(): Observable<DataEndpoint<UserDetailModel>> {
    if (this.isLoadingUser()) {
      return this.user$.pipe(
        take(1),
        map(user => ({
          Status: 200,
          Data: user,
          Message: ''
        } as DataEndpoint<UserDetailModel>))
      );
    }

    this.isLoadingUser.set(true);

    return this.userApi.getUserProfile().pipe(
      tap((response: DataEndpoint<UserDetailModel>) => {
        if (response.Status === 200 && response.Data) {
          this.userSubject.next(response.Data);
          this.userSignal.set(response.Data);
          this.isInitialized.set(true);
        }
        this.isLoadingUser.set(false);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading user profile:', error);
        this.isLoadingUser.set(false);
        this.isInitialized.set(true);
        this.clearUserData();
        return throwError(() => error);
      })
    );
  }

  getToken(): string | null {
    return (
      localStorage.getItem(this.TOKEN_KEY) ||
      sessionStorage.getItem(this.TOKEN_KEY)
    );
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && !this.isTokenExpired();
  }

  logout(): void {
    this.clearUserData();
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REMEMBER_ME_KEY);
    localStorage.removeItem(this.USERNAME_KEY);
    localStorage.removeItem(this.CONVERSION_KEY);
    localStorage.removeItem(this.GWP_KEY);
    localStorage.removeItem(this.BUSINESS_SELECTION_KEY);
    localStorage.removeItem(this.SELECTED_BUSINESS_KEY);
    this.businessStateService.clearPersistedSelection();
    this.isInitialized.set(false);
    this.router.navigate([Routes.loginPage]);
  }

  handleSessionExpired(): void {
    this.logout();
  }

  getDecodedToken(): TokenInformation | null {
    const token = this.getToken();
    return token ? this.decodeToken(token) : null;
  }

  getUserInfo(): any {
    const decoded = this.getDecodedToken();
    return decoded ?? null;
  }

  // Métodos para verificar roles
  hasRole(role: UserRole | string): boolean {
    return this.userSignal()?.Role === role;
  }

  hasAnyRole(roles: (UserRole | string)[]): boolean {
    const userRole = this.userSignal()?.Role;
    return roles.some(role => role === userRole);
  }

  // Métodos para verificar permisos
  hasPermission(permission: string): boolean {
    if (this.userSignal()?.Role === UserRole.ADMIN) return true;
    const permissions = this.userSignal()?.GrantedPermissions || [];
    return permissions.includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (this.userSignal()?.Role === UserRole.ADMIN) return true;
    const userPermissions = this.userSignal()?.GrantedPermissions || [];
    return permissions.some(p => userPermissions.includes(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    if (this.userSignal()?.Role === UserRole.ADMIN) return true;
    const userPermissions = this.userSignal()?.GrantedPermissions || [];
    return permissions.every(p => userPermissions.includes(p));
  }

  getUser(): UserDetailModel | null {
    return this.userSignal();
  }

  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private clearUserData(): void {
    this.userSubject.next(null);
    this.userSignal.set(null);
  }

  private isTokenExpired(): boolean {
    try {
      const decoded = this.getDecodedToken();
      if (!decoded || !decoded.exp) return true;

      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }
}
